import { useState, useMemo, useCallback } from "react";
import { InvoiceModel } from "@jugl-web/rest-api";

export interface UseSelectedItemsReturn {
  items: Set<string>;
  toggleItem: (id: string) => void;
  toggleAll: () => void;
  allSelected: boolean;
  unselectAll: () => void;
  visibleSelectedCount: number;
  totalSelectedCount: number;
  getVisibleSelectedItems: () => InvoiceModel[];
  getAllSelectedItems: () => InvoiceModel[];
}

export const useSelectedItems = (
  visibleData: InvoiceModel[] | null,
  allItems: InvoiceModel[] = []
): UseSelectedItemsReturn => {
  const [items, setSelected] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) => {
    setSelected((prev) => {
      const newSet = new Set(prev);

      const action = newSet.has(id) ? "delete" : "add";

      newSet[action](id);

      return newSet;
    });
  };

  const toggleAll = () => {
    if (!visibleData) return;

    setSelected((prev) => {
      // Check if all visible items are selected
      const visibleIds = visibleData.map(({ id }) => id);
      const allVisibleSelected = visibleIds.every((id) => prev.has(id));

      if (allVisibleSelected) {
        // Unselect all visible items but keep other selections
        const newSet = new Set(prev);
        visibleIds.forEach((id) => newSet.delete(id));
        return newSet;
      }
      // Select all visible items in addition to existing selections
      const newSet = new Set(prev);
      visibleIds.forEach((id) => newSet.add(id));
      return newSet;
    });
  };

  const unselectAll = () => {
    setSelected(new Set());
  };

  const getVisibleSelectedItems = useCallback(
    () => (visibleData ? visibleData.filter((item) => items.has(item.id)) : []),
    [visibleData, items]
  );

  const getAllSelectedItems = () =>
    allItems.filter((item) => items.has(item.id));

  const visibleSelectedCount = useMemo(
    () => getVisibleSelectedItems().length,
    [getVisibleSelectedItems]
  );

  const totalSelectedCount = useMemo(() => items.size, [items]);

  return {
    items,
    toggleItem,
    toggleAll,
    unselectAll,
    allSelected: visibleData
      ? visibleData.every((item) => items.has(item.id))
      : false,
    visibleSelectedCount,
    totalSelectedCount,
    getVisibleSelectedItems,
    getAllSelectedItems,
  };
};
